import { DropRectangle, SeedLayerDTO } from 'business/models/CustomModels';
import { DragRectangleProps, DragWellDetail } from './constants';
import {
  FragmentLayerDTO,
  LayerDTO,
  ProteinInfoDTO,
  ProteinLayerDTO,
  ScreenLayerDTO,
} from 'business/models/Models';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import { getProteinLayerProteinFromualtion } from 'components/commandpanel/ProteinLayer/utils';
import { GetGlobalExpData, GetScreenById, GetSeedList } from 'utils/Global';
import type { RefObject } from 'react';
import Konva from 'konva';
import FragmentService from 'business/services/FragmentService';
import ExperimentService from 'business/services/ExperimentService';
import { getUniqueLayerName } from 'business/plateviewer/GenerateUtil';
import {
  getMultipleFragmentLayersErrorMessage,
  isFragmentLayerAlreadyInDrop,
} from 'business/plateviewer/Generate/GenerateCommon';
import { Rectangle } from 'components/canvas/constants';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import { copyScreenLayerToCustomlayer } from '../Optimization/IterativeScreenOptimizationHelper';

const BUFFER_AREA = 3;

type OverlayResult = {
  dragRectProps: DragRectangleProps;
  dragWellDetail?: DragWellDetail;
};

export function getOverlayRectangleProps(
  stageRef: RefObject<Konva.Stage>,
  clientX: number,
  clientY: number,
  isDropTarget: boolean,
): OverlayResult {
  const stage = stageRef.current;
  const dragRectProps = new DragRectangleProps();

  if (!stage) {
    return { dragRectProps };
  }

  // Translate mouse position into stage coordinates
  const { left, top } = stage.container().getBoundingClientRect();
  const posClient = { x: clientX - left, y: clientY - top };

  const pos = stage.getAbsoluteTransform().copy().invert().point(posClient);

  // Top-most shape under the pointer
  const wellShape = stage.getIntersection(pos) as Konva.Shape | null;
  if (!wellShape) {
    dragRectProps.visible = false;
    return { dragRectProps };
  }

  const { drops, wellPositionDetails, wellNum, x, y, width, height, plateNum, hasError } =
    wellShape.attrs;

  dragRectProps.visible = true;
  const dragWellDetail: DragWellDetail = {
    ...wellPositionDetails,
    wellNumber: wellNum,
    plateNumber: plateNum,
  };

  // If we are targeting a drop area, check if the mouse is inside a drop rectangle
  if (isDropTarget && drops) {
    const hoveredDrop: DropRectangle | undefined = drops.find((drop) =>
      isMouseOnRectangle(
        pos,
        { x: drop.x, y: drop.y },
        wellShape.attrs.height,
        drop.w,
        BUFFER_AREA,
      ),
    );

    if (hoveredDrop) {
      const { x: dx, y: dy, w, h, dropNumber } = hoveredDrop;
      Object.assign(dragRectProps, { x: dx, y: dy, width: w, height: h });
      dragWellDetail.dropNumber = dropNumber;
      return { dragRectProps, dragWellDetail };
    } else if (drops.length === 0 && hasError) {
      dragWellDetail.dropNumber = 1;
    }
  } else {
    Object.assign(dragRectProps, { x, y, width, height });
  }

  return { dragRectProps, dragWellDetail };
}

export const isMouseOnRectangle = (
  mousePoint,
  rectPoint,
  height,
  width,
  bufferZone = 0,
) => {
  if (
    mousePoint.x >= rectPoint.x + bufferZone &&
    mousePoint.x <= rectPoint.x + width + bufferZone
  ) {
    if (
      mousePoint.y + bufferZone >= rectPoint.y &&
      mousePoint.y <= rectPoint.y + height + bufferZone
    ) {
      return true;
    }
  }
  return false;
};

export const addProteinLayerDetails = (
  dropNumber: number,
  newLayer: LayerDTO,
  exp: ExperimentDtoClient,
  nodeId: number,
  fromLayersExplorer: boolean = false,
) => {
  newLayer.targetDrop = dropNumber;
  const protein = getProteinInfoDTO(nodeId);
  protein.dropVolume = exp.containerDefDropVolume / 2.0;
  protein.proteinVolume = exp.containerDefDropVolume / 2.0;
  newLayer.name = getUniqueLayerName(exp.layers, protein.name);
  const detail = getProteinLayerProteinFromualtion(
    newLayer.layerDetail as ProteinLayerDTO,
    protein,
    newLayer.id,
  );

  if (fromLayersExplorer) {
    detail.startProteinDropVolume = null;
    detail.stopProteinDropVolume = null;
    detail.startWellDropVolume = null;
    detail.stopWellDropVolume = null;
  }
  (newLayer.layerDetail as ProteinLayerDTO).proteinLayerProteinFormulation = [detail];
  return newLayer;
};

const getProteinInfoDTO = (nodeId: number) => {
  const proteinFormulation = GetGlobalExpData()?.proteinFormulationList?.find(
    (pf) => pf.treeNodeId === nodeId,
  );
  const protein = new ProteinInfoDTO();
  protein.id = proteinFormulation.treeNodeId;
  protein.pfId = proteinFormulation.id;
  protein.name = proteinFormulation.pfName;
  return protein;
};

export const getSeedLayerDetails = (
  layer: LayerDTO,
  dropNumber: number,
  node: any,
  exp: ExperimentDtoClient,
  fromLayersExplorer: boolean = false,
) => {
  const seed = GetSeedList().find((s) => s.treeNodeId === node.id);
  layer.targetDrop = dropNumber;
  const seedLayerDetail = layer.layerDetail as SeedLayerDTO;
  seedLayerDetail.layerName = getUniqueLayerName(exp.layers, node.name);
  seedLayerDetail.seedDropVolumeVaryAlong = 1;

  if (fromLayersExplorer) {
    seedLayerDetail.startSeedDropVolume = null;
    seedLayerDetail.stopSeedDropVolume = null;
  } else {
    seedLayerDetail.startSeedDropVolume = exp.containerDefSeedVolume;
    seedLayerDetail.stopSeedDropVolume = exp.containerDefSeedVolume;
  }
  seedLayerDetail.seedId = seed.id;
  return layer;
};

export const getFragmentlayerDetails = async (
  layer: LayerDTO,
  dropNumber: number,
  fragmentid: number,
  name: string,
  exp: ExperimentDtoClient,
  fromLayersExplorer: boolean = false,
) => {
  if (isFragmentLayerAlreadyInDrop(exp, layer, dropNumber)) {
    throw new Error(getMultipleFragmentLayersErrorMessage(dropNumber));
  }

  layer.targetDrop = dropNumber;
  const fragmentScreen = await new FragmentService().getFragmentScreenData(fragmentid);
  const detail = layer.layerDetail as FragmentLayerDTO;
  detail.fragmentScreenId = fragmentScreen.id;
  detail.fragmentScreen = fragmentScreen;
  detail.volume = fromLayersExplorer ? null : layer.volume;
  layer.name = getUniqueLayerName(exp.layers, name);
  return layer;
};

export const getScreenLayerDetails = async (
  layer: LayerDTO,
  dropNumber: number,
  screenid: number,
  name: string,
  exp: ExperimentDtoClient,
) => {
  const screenLotId = GetScreenById(screenid)?.screenLot[0]?.id;
  const screenLotDetails = await new ExperimentService().getScreenLotDetails(screenLotId);
  if (dropNumber) {
    layer.targetDrop = dropNumber;
  }
  (layer.layerDetail as ScreenLayerDTO).screenLot = screenLotDetails;
  layer.name = getUniqueLayerName(exp.layers, name);
  return layer;
};

export const getLayerRectDimensions = (
  exp: ExperimentDtoClient,
  type: ETreeNodeTypes,
  dragWellDetail: DragWellDetail,
  usedWellCount: number,
) => {
  let layerRect: Rectangle = { x: 0, y: 0, height: 0, width: 0 };
  switch (type) {
    case ETreeNodeTypes.Screen:
    case ETreeNodeTypes.FragmentScreen:
      layerRect = getCurrentLayerRectangle(exp, dragWellDetail, usedWellCount);
      break;
    default:
      layerRect = getWholeCanvasRectangle(exp);
      break;
  }
  return layerRect;
};

export const getCurrentLayerRectangle = (
  exp: ExperimentDtoClient,
  dragWellDetail: DragWellDetail,
  usedWellCount: number,
) => {
  const layerDimension = getScreenLayerDimensions(exp, dragWellDetail, usedWellCount);

  // Get Layer Starting well plate, and the canvas rectangle for that well
  let plateWellX = layerDimension.x % exp.containerCols;
  let plateWellY = layerDimension.y % exp.containerRows;
  const currentPlate = exp.plateDto.find(
    (plate) => plate.plateNumber === dragWellDetail.plateNumber,
  );
  if (!currentPlate) {
    return null;
  }
  const startWell = currentPlate.wellRects[plateWellX + plateWellY * exp.containerCols];

  // Get Layer Ending well plate, and the canvas rectangle for that well
  const endingPlateCol = Math.ceil(
    (layerDimension.x + layerDimension.width + 1) / exp.containerCols,
  );
  const endingPlateRow = Math.ceil(
    (layerDimension.y + layerDimension.height + 1) / exp.containerRows,
  );
  const endingPlate = exp.plateDto.find(
    (p) => p.colPos === endingPlateCol && p.rowPos === endingPlateRow,
  );

  plateWellX = (layerDimension.x + layerDimension.width) % exp.containerCols;
  plateWellY = (layerDimension.y + layerDimension.height) % exp.containerRows;
  const endWell = endingPlate.wellRects[plateWellX + plateWellY * exp.containerCols];
  return {
    x: startWell.x,
    y: startWell.y,
    height: endWell.y - startWell.y + endWell.h,
    width: endWell.x - startWell.x + endWell.w,
  } as Rectangle;
};

export const getWholeCanvasRectangle = (exp: ExperimentDtoClient) => {
  const lastPlate = exp.plateDto.reduce((max, item) =>
    item.plateNumber > max.plateNumber ? item : max,
  );
  const firstPlate = exp.plateDto.find((plate) => plate.plateNumber === 1);

  const startWell = firstPlate.wellRects[0];
  const endWell = lastPlate.wellRects[lastPlate.wellRects.length - 1];
  return {
    x: startWell.x,
    y: startWell.y,
    height: endWell.y - startWell.y + endWell.h,
    width: endWell.x - startWell.x + endWell.w,
  } as Rectangle;
};

export const getLayerDimensions = (
  exp: ExperimentDtoClient,
  nodeType: ETreeNodeTypes,
  dragWellDetail: DragWellDetail,
  usedWellCount: number = 0,
) => {
  const { containerRows, containerCols, rowCount, colCount } = exp;
  let layerDimensions: Rectangle = { x: 0, y: 0, height: 0, width: 0 };
  switch (nodeType) {
    case ETreeNodeTypes.Screen:
    case ETreeNodeTypes.FragmentScreen:
      layerDimensions = getScreenLayerDimensions(exp, dragWellDetail, usedWellCount);
      break;
    default:
      layerDimensions.width = containerCols * colCount - 1;
      layerDimensions.height = containerRows * rowCount - 1;
      break;
  }
  return layerDimensions;
};

const getScreenLayerDimensions = (
  exp: ExperimentDtoClient,
  dragWellDetail: DragWellDetail,
  usedWellCount,
) => {
  const canvasRect: Rectangle = {
    x: dragWellDetail.x,
    y: dragWellDetail.y,
    width: exp.containerCols,
    height: exp.containerRows,
  };

  if (usedWellCount <= canvasRect.width) {
    canvasRect.height = 1;
    canvasRect.width = usedWellCount;
  } else if (usedWellCount % canvasRect.width == 0) {
    canvasRect.height = Math.floor(usedWellCount / canvasRect.width);
  } else {
    canvasRect.height = Math.floor(usedWellCount / canvasRect.width) + 1;
  }

  // Handle overflowing start position
  const overflowX = canvasRect.x + canvasRect.width - exp.colCount * exp.containerCols;
  const overflowY = canvasRect.y + canvasRect.height - exp.rowCount * exp.containerRows;

  if (overflowX > 0) {
    canvasRect.x = Math.max(0, canvasRect.x - overflowX);
  }
  if (overflowY > 0) {
    canvasRect.y = Math.max(0, canvasRect.y - overflowY);
  }

  // handle overflowing height and width
  const maxWidth = exp.containerCols * exp.rowCount;
  const maxHeight = exp.containerRows * exp.colCount;
  canvasRect.height = Math.min(canvasRect.height, maxHeight);
  canvasRect.width = Math.min(canvasRect.width, maxWidth);

  // Adjust width/height as canvas considers 0 for 1 well height/width
  if (canvasRect.height > 0) {
    canvasRect.height--;
  }
  if (canvasRect.width > 0) {
    canvasRect.width--;
  }

  return canvasRect;
};

export const addLayerDetails = async (
  type: ETreeNodeTypes,
  dropNumber: number,
  layer: LayerDTO,
  exp: ExperimentDtoClient,
  id: number,
  node,
  extra,
  isScreenRoute,
  fromLayersExplorer: boolean = false,
) => {
  switch (type) {
    case ETreeNodeTypes.Protein: {
      if (!dropNumber) {
        return;
      }
      layer = addProteinLayerDetails(dropNumber, layer, exp, id, fromLayersExplorer);
      break;
    }
    case ETreeNodeTypes.Screen: {
      layer = await getScreenLayerDetails(layer, dropNumber, extra?.id, node.name, exp);
      if (isScreenRoute) {
        layer = copyScreenLayerToCustomlayer(layer, exp.experimentId, false);
        layer.name = getUniqueLayerName(exp.layers, 'Custom Layer');
      }
      break;
    }
    case ETreeNodeTypes.FragmentScreen: {
      if (!dropNumber) {
        return;
      }
      layer = await getFragmentlayerDetails(
        layer,
        dropNumber,
        extra?.id,
        node.name,
        exp,
        fromLayersExplorer,
      );
      break;
    }
    case ETreeNodeTypes.Seed: {
      if (!dropNumber) {
        return;
      }
      layer = getSeedLayerDetails(layer, dropNumber, node, exp, fromLayersExplorer);
      break;
    }
    default:
      // No action required for other node types
      break;
  }
  return layer;
};

const layerDragDropAllowedTypes = [
  ETreeNodeTypes.Screen,
  ETreeNodeTypes.FragmentScreen,
  ETreeNodeTypes.Protein,
  ETreeNodeTypes.Seed,
];
export const isLayerDragDropAllowedType = (type: ETreeNodeTypes) => {
  return layerDragDropAllowedTypes.includes(type);
};

export const isDraggingCurrentScreen = (
  node: { type?: ETreeNodeTypes; id?: number } | null | undefined,
  exp: ExperimentDtoClient,
  isScreenRoute?: boolean,
): boolean => {
  return (
    !!isScreenRoute &&
    node?.type === ETreeNodeTypes.Screen &&
    exp?.treeNodeId != null &&
    node.id === exp.treeNodeId
  );
};
